import { useState, useCallback } from 'react';
import type { IdentityDocumentStatus, IdentityDocumentSummary } from '../../../../modules/identity/types';
import {
  getIdentityStatusConfig,
  canVerifyIdentity,
  canAssistUpload,
  getDocumentTypeLabel,
} from '../../../../modules/identity/helpers';
import {
  verifyIdentityDocument,
  rejectIdentityDocument,
  requestIdentityUploadSession,
  completeIdentityUpload,
} from '../../../../services/identityGatewayService';
import type { IdentityDocument } from '../../../../modules/identity/types';
import { ViewIdentityDocumentModal } from './ViewIdentityDocumentModal';

interface IdentityDocumentSectionProps {
  customerId: string;
  customerName: string;
  /** 傳入原有 IdentityDocument 陣列（可為空陣列，主要透過 summary 取狀態與 doc IDs） */
  documents: IdentityDocument[];
  summary: IdentityDocumentSummary;
  adminRole: string | null | undefined;
  onDocumentsChange?: () => void;
}

/**
 * Admin 客戶編輯 modal 內的身分證件區塊
 *
 * Phase 2: 改接 Railway identity gateway
 */
export function IdentityDocumentSection({
  customerId,
  customerName,
  summary,
  adminRole,
  onDocumentsChange,
}: IdentityDocumentSectionProps): JSX.Element {
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewDocId, setViewDocId] = useState<string | null>(null);
  const [viewDocType, setViewDocType] = useState<'front' | 'back'>('front');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectDocId, setRejectDocId] = useState<string | null>(null);
  const [rejectDocType, setRejectDocType] = useState<'front' | 'back'>('front');
  const [rejectReason, setRejectReason] = useState('');
  const [uploading, setUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const allowVerify = canVerifyIdentity(adminRole);
  const allowUpload = canAssistUpload(adminRole);

  // 建立一個臨時 IdentityDocument 物件供 ViewModal 使用
  const makeViewDocument = (docId: string, type: 'front' | 'back'): IdentityDocument => ({
    id: docId,
    customer_id: customerId,
    document_type: type,
    storage_path: '',
    status: type === 'front' ? summary.frontStatus : summary.backStatus,
    verified_by: null,
    verified_by_name: null,
    verified_at: summary.lastVerifiedAt,
    rejection_reason: null,
    created_at: summary.lastUploadedAt || '',
    updated_at: summary.lastUploadedAt || '',
  });

  const handleViewDocument = useCallback((docId: string, type: 'front' | 'back') => {
    setViewDocId(docId);
    setViewDocType(type);
    setViewModalOpen(true);
    setBackendError(null);
  }, []);

  const handleVerify = useCallback(
    async (docId: string, type: 'front' | 'back', action: 'verify' | 'reject' | 'mask') => {
      setBackendError(null);
      setSuccessMsg(null);
      if (!allowVerify) {
        setBackendError('您沒有權限執行此操作');
        return;
      }

      if (action === 'reject') {
        setRejectDocId(docId);
        setRejectDocType(type);
        setRejectModalOpen(true);
        return;
      }

      setActionLoading(true);
      try {
        const adminId = localStorage.getItem('adminId') || '';
        const adminName = localStorage.getItem('adminName') || '管理員';

        if (action === 'verify') {
          await verifyIdentityDocument({ documentId: docId, verifierId: adminId, verifierName: adminName });
          setSuccessMsg(`${getDocumentTypeLabel(type)} 已核驗通過`);
        }
        // mask 尚未有獨立 endpoint，使用 reject 以 'mask' 原因
        if (action === 'mask') {
          await rejectIdentityDocument({ documentId: docId, reason: 'masked', verifierId: adminId, verifierName: adminName });
          setSuccessMsg(`${getDocumentTypeLabel(type)} 已遮蔽`);
        }
        onDocumentsChange?.();
      } catch (err: any) {
        setBackendError(err.message || '操作失敗');
      } finally {
        setActionLoading(false);
      }
    },
    [allowVerify, onDocumentsChange]
  );

  const handleConfirmReject = useCallback(async () => {
    if (!rejectDocId || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      const adminId = localStorage.getItem('adminId') || '';
      const adminName = localStorage.getItem('adminName') || '管理員';
      await rejectIdentityDocument({
        documentId: rejectDocId,
        reason: rejectReason.trim(),
        verifierId: adminId,
        verifierName: adminName,
      });
      setSuccessMsg(`${getDocumentTypeLabel(rejectDocType)} 已拒絕`);
      setRejectModalOpen(false);
      setRejectDocId(null);
      setRejectReason('');
      onDocumentsChange?.();
    } catch (err: any) {
      setBackendError(err.message || '拒絕操作失敗');
    } finally {
      setActionLoading(false);
    }
  }, [rejectDocId, rejectDocType, rejectReason, onDocumentsChange]);

  const handleUpload = useCallback(
    async (type: 'front' | 'back', file: File) => {
      if (!allowUpload) {
        setBackendError('您沒有權限協助上傳證件');
        return;
      }
      setUploading(true);
      setBackendError(null);
      setSuccessMsg(null);

      try {
        const adminId = localStorage.getItem('adminId');
        const session = await requestIdentityUploadSession({
          customerId,
          documentType: type,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          uploadedBy: 'staff_assist',
          uploaderId: adminId || undefined,
        });

        if (session.uploadUrl && session.uploadUrl.startsWith('http')) {
          const uploadResp = await fetch(session.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
          if (!uploadResp.ok) throw new Error(`檔案上傳失敗 (${uploadResp.status})`);
        }

        await completeIdentityUpload({
          sessionId: session.sessionId,
          customerId,
          documentType: type,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        });

        setSuccessMsg(`${getDocumentTypeLabel(type)} 協助上傳成功，狀態已更新為待驗證`);
        onDocumentsChange?.();
      } catch (err: any) {
        setBackendError(err.message || '上傳失敗，請稍後再試');
      } finally {
        setUploading(false);
      }
    },
    [allowUpload, customerId, onDocumentsChange]
  );

  const handleFileInputChange = useCallback(
    (type: 'front' | 'back') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setBackendError('僅接受圖片檔案（jpg, png, webp）');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setBackendError('檔案大小不可超過 10MB');
        return;
      }
      handleUpload(type, file);
      e.target.value = '';
    },
    [handleUpload]
  );

  const renderDocCard = (type: 'front' | 'back', status: IdentityDocumentStatus, docId?: string) => {
    const config = getIdentityStatusConfig(status);
    const hasDoc = status !== 'not_uploaded';

    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${status === 'verified' ? 'bg-emerald-500' : status === 'pending' ? 'bg-amber-500' : status === 'rejected' ? 'bg-red-500' : 'bg-gray-300'}`}></span>
            <span className="text-sm font-semibold text-gray-800">{getDocumentTypeLabel(type)}</span>
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
            <i className={`${config.icon}`}></i>
            {config.label}
          </span>
        </div>

        {hasDoc && docId ? (
          <div className="space-y-2">
            <div className="text-xs text-gray-500 space-y-0.5">
              {summary.lastUploadedAt && (
                <div>上傳時間：{new Date(summary.lastUploadedAt).toLocaleString('zh-TW')}</div>
              )}
              {summary.lastVerifiedAt && (
                <div>審核時間：{new Date(summary.lastVerifiedAt).toLocaleString('zh-TW')}</div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => handleViewDocument(docId, type)}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line mr-1"></i>
                查看證件
              </button>

              {allowVerify && status === 'pending' && (
                <>
                  <button
                    onClick={() => handleVerify(docId, type, 'verify')}
                    disabled={actionLoading}
                    className="px-3 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-200 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-checkbox-circle-line mr-1"></i>
                    核驗通過
                  </button>
                  <button
                    onClick={() => handleVerify(docId, type, 'reject')}
                    disabled={actionLoading}
                    className="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-close-circle-line mr-1"></i>
                    拒絕
                  </button>
                </>
              )}

              {allowVerify && status === 'verified' && (
                <button
                  onClick={() => handleVerify(docId, type, 'mask')}
                  disabled={actionLoading}
                  className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-eye-off-line mr-1"></i>
                  遮蔽
                </button>
              )}
            </div>
          </div>
        ) : hasDoc && !docId ? (
          /* 有狀態但無 document ID（gateway 未返回） */
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">証件已上傳，詳細資訊由 gateway 管理</p>
          </div>
        ) : (
          /* 未上傳 */
          <div className="space-y-3">
            <div className="h-24 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center">
              <span className="text-sm text-gray-400">尚未上傳</span>
            </div>
            {allowUpload && (
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange(type)}
                  disabled={uploading}
                  className="hidden"
                />
                <span className="flex items-center justify-center gap-2 px-3 py-2 bg-teal-50 text-teal-700 text-xs font-medium rounded-lg hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap border border-teal-200">
                  {uploading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      上傳中...
                    </>
                  ) : (
                    <>
                      <i className="ri-upload-cloud-2-line"></i>
                      協助上傳 {getDocumentTypeLabel(type)}
                    </>
                  )}
                </span>
              </label>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* 整體狀態摘要 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <i className="ri-shield-user-line text-xl text-gray-600"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">身分證件</h3>
            <p className="text-xs text-gray-500">
              正面：{summary.frontUploaded ? '已上傳' : '未上傳'} ·
              反面：{summary.backUploaded ? '已上傳' : '未上傳'}
            </p>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
            summary.identityReady
              ? 'bg-emerald-100 text-emerald-600'
              : `${getIdentityStatusConfig(summary.overallStatus).bgColor} ${getIdentityStatusConfig(summary.overallStatus).color}`
          }`}
        >
          <i className={summary.identityReady ? 'ri-checkbox-circle-fill' : getIdentityStatusConfig(summary.overallStatus).icon}></i>
          整體：{summary.identityReady ? '已驗證' : getIdentityStatusConfig(summary.overallStatus).label}
        </span>
      </div>

      {/* 成功訊息 */}
      {successMsg && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-xs text-emerald-700">
            <i className="ri-checkbox-circle-line mr-1"></i>
            {successMsg}
          </p>
        </div>
      )}

      {/* 錯誤訊息 */}
      {backendError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-600">
            <i className="ri-error-warning-line mr-1"></i>
            {backendError}
          </p>
        </div>
      )}

      {/* 證件卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderDocCard('front', summary.frontStatus, summary.frontDocumentId)}
        {renderDocCard('back', summary.backStatus, summary.backDocumentId)}
      </div>

      {/* 拒絕原因彈窗 */}
      {rejectModalOpen && rejectDocId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-close-circle-line text-red-600"></i>
                拒絕證件
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                拒絕 <span className="font-semibold">{getDocumentTypeLabel(rejectDocType)}</span>
                ，請填寫拒絕原因供客戶參考：
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="例如：影像模糊、資訊不完整、與登記資料不符..."
                rows={3}
                maxLength={200}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
              />
              <p className="text-xs text-gray-400">{rejectReason.length}/200</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setRejectDocId(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={handleConfirmReject}
                disabled={!rejectReason.trim() || actionLoading}
                className="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                確認拒絕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 查看證件彈窗 */}
      {viewModalOpen && viewDocId && (
        <ViewIdentityDocumentModal
          document={makeViewDocument(viewDocId, viewDocType)}
          customerName={customerName}
          onClose={() => {
            setViewModalOpen(false);
            setViewDocId(null);
          }}
        />
      )}
    </div>
  );
}